import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Shared presentational primitives for the redesigned Export Hub product detail
 * page. These mirror the structural components the Import Hub product page
 * defines inline, rebuilt here in the export module's own conventions
 * (MUI icons + export-hub.css classes + real CSS variables) so the two pages
 * read as siblings without dragging the import module's lucide/Tailwind kit in.
 *
 * Severity maps to the REAL tokens (spec's --success/--warning/--danger/--info
 * do not exist): success=--full-bar-color, warning=--mid-color,
 * danger=--low-color, info=--action-color/--count-color. See export-hub.css.
 */
export type Severity = 'success' | 'info' | 'warning' | 'danger';

export const sevPillClass = (sev: Severity): string => `eh-pill eh-pill-${sev}`;
export const sevTextClass = (sev: Severity): string => `eh-sev-${sev}`;
export const sevBoxClass = (sev: Severity): string => `eh-sev-box eh-sev-box-${sev}`;

/** Small icon tile + big title + subtitle section header with an action slot. */
export const PageSection: React.FC<{
  id: string;
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, icon: Icon, title, subtitle, action, children }) => (
  <section id={id} className='eh-pd-section'>
    <div className='eh-pd-section-head'>
      <div className='eh-pd-section-headline'>
        <span className='eh-pd-section-icon'>
          <Icon style={{ fontSize: 20 }} />
        </span>
        <div>
          <h2 className='eh-pd-section-title'>{title}</h2>
          {subtitle && <p className='eh-pd-section-sub'>{subtitle}</p>}
        </div>
      </div>
      {action && <div className='eh-pd-section-action'>{action}</div>}
    </div>
    <div className='eh-pd-section-body'>{children}</div>
  </section>
);

/** "Get latest results" affordance used in section headers. */
export const SectionRefreshBtn: React.FC<{
  loading?: boolean;
  onClick?: () => void;
}> = ({ loading, onClick }) => (
  <button
    type='button'
    className='eh-mini-btn'
    onClick={onClick}
    disabled={loading}
  >
    <RefreshIcon className={loading ? 'eh-spin' : undefined} style={{ fontSize: 14 }} />
    {loading ? 'Refreshing…' : 'Get latest results'}
  </button>
);

/** A small header action button (icon + label). Primary = filled brand. */
export const HeaderBtn: React.FC<{
  icon?: React.ElementType;
  primary?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ icon: Icon, primary, disabled, onClick, children }) => (
  <button
    type='button'
    className={primary ? 'btn-primary' : 'btn-outline'}
    disabled={disabled}
    onClick={onClick}
  >
    {Icon && <Icon style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }} />}
    {children}
  </button>
);

export const QUICK_NAV_ITEMS = [
  { id: 'classification', label: 'Classification' },
  { id: 'jurisdiction', label: 'Export Control Regime' },
  { id: 'screening', label: 'Risk Screening' },
  { id: 'licenses', label: 'Licenses' },
  { id: 'entities', label: 'Entities' },
] as const;

/**
 * Combined height (px) of the sticky chrome above a section: the app header
 * (.Top-Header, 64px) plus the sticky quick-nav (~48px). Jump-to scrolling and
 * active-section detection both offset by this so a section lands just below
 * the quick-nav, not underneath it.
 */
const QUICK_NAV_OFFSET = 112;

/** Sticky "Jump to" anchor nav with active-section highlighting on scroll. */
export const QuickNav: React.FC = () => {
  const [active, setActive] = useState<string>(QUICK_NAV_ITEMS[0].id);

  useEffect(() => {
    const els = QUICK_NAV_ITEMS.map((i) => document.getElementById(i.id)).filter(
      Boolean,
    ) as HTMLElement[];
    if (!els.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              (a.target as HTMLElement).offsetTop -
              (b.target as HTMLElement).offsetTop,
          );
        if (visible[0]) setActive((visible[0].target as HTMLElement).id);
      },
      { rootMargin: '-112px 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const jump = (id: string) => {
    const el = document.getElementById(id);
    if (el)
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - QUICK_NAV_OFFSET,
        behavior: 'smooth',
      });
  };

  return (
    <div className='eh-quicknav'>
      <span className='eh-quicknav-label'>Jump to</span>
      {QUICK_NAV_ITEMS.map((it) => (
        <a
          key={it.id}
          href={`#${it.id}`}
          className={`eh-quicknav-pill${active === it.id ? ' active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            jump(it.id);
          }}
        >
          {it.label}
        </a>
      ))}
    </div>
  );
};

/** Read-only labelled value (uppercase Inter label + Roboto value). */
export const Field: React.FC<{
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
}> = ({ label, value, mono }) => {
  const empty =
    value === undefined || value === null || value === '' || value === '—';
  return (
    <div className='eh-field-cell'>
      <div className='eh-field-cell-label'>{label}</div>
      <div className={`eh-field-cell-value${mono ? ' eh-mono' : ''}${empty ? ' eh-field-cell-empty' : ''}`}>
        {empty ? '—' : value}
      </div>
    </div>
  );
};

/** Editable labelled value: input/textarea when editing, else read-only Field. */
export const EditableField: React.FC<{
  editing: boolean;
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  multiline?: boolean;
  placeholder?: string;
}> = ({ editing, label, value, onChange, mono, multiline, placeholder }) => {
  if (!editing) return <Field label={label} value={value} mono={mono} />;
  return (
    <label className='eh-field-cell'>
      <span className='eh-field-cell-label'>{label}</span>
      {multiline ? (
        <textarea
          className={`eh-input eh-textarea${mono ? ' eh-mono' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
        />
      ) : (
        <input
          className={`eh-input${mono ? ' eh-mono' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </label>
  );
};

/** Right-side slide-over drawer (~520px). Header = eyebrow + title + close X. */
export const DrawerShell: React.FC<{
  open: boolean;
  eyebrow: string;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, eyebrow, title, onClose, children }) => {
  if (!open) return null;
  return (
    <div className='eh-drawer-root'>
      <div className='eh-drawer-backdrop' onClick={onClose} />
      <aside className='eh-drawer' role='dialog' aria-label={title}>
        <header className='eh-drawer-head'>
          <div>
            <div className='eh-drawer-eyebrow'>{eyebrow}</div>
            <h3 className='eh-drawer-title'>{title}</h3>
          </div>
          <button
            type='button'
            className='eh-drawer-close'
            onClick={onClose}
            aria-label='Close'
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </header>
        <div className='eh-drawer-body'>{children}</div>
      </aside>
    </div>
  );
};
